import React, { useCallback, useEffect, useRef, useState } from 'react';
const API = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
const ROOT = `${API}/cold-outreach`;
const PREFIX = 'tribe.coldOutreach.';
const STATUSES = {
  new: 'New',
  sent: 'Sent',
  skipped: 'Skipped',
  couldnt_send: "Couldn't send",
  all: 'All contacts'
};

const FIT_STATUSES = {
  unreviewed: 'Unreviewed',
  good_fit: 'Good fit',
  not_fit: 'Not a fit'
};

const VIEWS = {
  needs_human: {
    label: 'Needs human',
    bucket: 'needs_human',
    description: 'AI finished reviewing these profiles but did not have enough confidence to make the final call.'
  },
  ai_rejected: {
    label: 'AI rejected',
    bucket: 'ai_rejected',
    description: 'Profiles the AI automatically excluded. Audit these here and approve anyone it got wrong.'
  },
  ready: {
    label: 'Ready to send',
    bucket: 'ready',
    description: 'Approved people waiting for outreach.'
  },
  contacted: {
    label: 'Contacted',
    bucket: 'contacted',
    description: 'People who have already been contacted.'
  },
  manual_not_fit: {
    label: 'Manual not a fit',
    bucket: 'manual_not_fit',
    description: 'People you manually excluded from outreach.'
  },
  awaiting_ai: {
    label: 'Awaiting AI',
    bucket: 'awaiting_ai',
    description: 'Unreviewed profiles that are waiting to be captured, queued, or processed by AI.'
  },
  ai_errors: {
    label: 'AI errors',
    bucket: 'ai_errors',
    description: 'Profiles whose AI review failed and can be retried.'
  },
  all: {
    label: 'All contacts',
    bucket: 'all',
    description: 'Every contact in the shared outreach database.'
  }
};

const DEFAULT_MESSAGE = "Hey, you seem cool! I’m part of an invite-only social club called Tribe, and they just gave members a few invites to share. You seem like you’d be a great fit, so I wanted to offer you one 😊";

const EMPTY_STATS = {
  totals: {},
  review: {},
  ai: {},
  buckets: {},
  daily: [],
  sources: []
};
function readSaved(key, fallback, session = false) {
  try {
    return JSON.parse((session ? sessionStorage : localStorage).getItem(PREFIX + key)) ?? fallback;
  } catch {
    return fallback;
  }
}
function save(key, value, session = false) {
  try {
    (session ? sessionStorage : localStorage).setItem(PREFIX + key, JSON.stringify(value));
  } catch {/* Preferences are optional; contact history lives on the server. */}
}
function normalizeHandle(value) {
  return String(value || '').trim().replace(/^@/, '').toLowerCase();
}
function validHandle(value) {
  return /^[a-z0-9._]{1,30}$/.test(value);
}
function detectSource(filename) {
  const match = filename.match(/^(.+?)_(?:followers|following)(?:_|\.)/i);
  return match ? normalizeHandle(match[1]) : '';
}
function csvHeaders(csv) {
  const headers = [];
  let field = '',
    quoted = false;
  const value = csv.replace(/^\uFEFF/, '');
  for (let i = 0; i < value.length; i += 1) {
    const c = value[i];
    if (c === '"') {
      if (quoted && value[i + 1] === '"') {
        field += '"';
        i += 1;
      } else quoted = !quoted;
    } else if (!quoted && (c === ',' || c === '\n' || c === '\r')) {
      headers.push(field.trim());
      field = '';
      if (c !== ',') return headers;
    } else field += c;
  }
  if (field) headers.push(field.trim());
  return headers;
}
function dateLabel(value) {
  return value ? new Date(value).toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  }) : '';
}

function formatCount(value) {
  return Number.isFinite(value) ? value.toLocaleString() : '—';
}

function percentLabel(value) {
  return Number.isFinite(value) ? `${Math.round(value * 100)}%` : '—';
}

function aiDecisionLabel(review) {
  if (!review) return '';
  if (review.decision === 'good_fit') return 'Good fit';
  if (review.decision === 'not_fit') return 'Not a fit';
  return 'Needs human';
}

function aiMethodLabel(review) {
  if (!review) return '';
  return review.model === 'fast-prepass-v1' ? 'Fast prepass' : 'Vision AI';
}

function topAiReason(review) {
  return Array.isArray(review?.reasons) && review.reasons.length
    ? review.reasons[0]
    : '';
}

function initials(contact) {
  const value = String(contact?.fullName || contact?.username || '?').trim();
  const parts = value.split(/\s+/).filter(Boolean);
  return (parts.length > 1 ? `${parts[0][0]}${parts[1][0]}` : value.slice(0, 2)).toUpperCase();
}

function reviewState(contact) {
  const fit = contact.fitStatus || 'unreviewed';
  const review = contact.aiReview;

  if (contact.aiReviewStatus === 'error' && fit === 'unreviewed') {
    return {
      label: 'AI error',
      detail: contact.aiReviewError || 'Review failed',
      kind: 'error'
    };
  }

  if (fit === 'unreviewed' && review?.analyzedAt) {
    return {
      label: 'Needs human',
      detail: `${aiMethodLabel(review)} · ${percentLabel(review.confidence)}`,
      kind: 'human'
    };
  }

  if (fit === 'not_fit' && review?.decision === 'not_fit' && review?.autoApplied) {
    return {
      label: 'AI rejected',
      detail: `${aiMethodLabel(review)} · ${percentLabel(review.confidence)}`,
      kind: 'rejected'
    };
  }

  if (fit === 'good_fit') {
    const aiApproved = review?.decision === 'good_fit' && review?.autoApplied;
    return {
      label: 'Good fit',
      detail: aiApproved ? `${aiMethodLabel(review)} · ${percentLabel(review.confidence)}` : 'Approved manually',
      kind: 'approved'
    };
  }

  if (fit === 'not_fit') {
    return {
      label: 'Not a fit',
      detail: 'Manual decision',
      kind: 'manual'
    };
  }

  const pipeline = contact.aiReviewStatus || 'not_started';
  return {
    label: 'Awaiting AI',
    detail: pipeline === 'processing' ? 'AI processing' : pipeline === 'queued' ? 'Queued for AI' : 'Waiting for capture',
    kind: 'pending'
  };
}

function copyMessage(message) {
  if (navigator.clipboard?.writeText) return navigator.clipboard.writeText(message);
  const field = document.createElement('textarea');
  field.value = message;
  field.style.position = 'fixed';
  field.style.opacity = '0';
  document.body.appendChild(field);
  field.select();
  const copied = document.execCommand('copy');
  field.remove();
  return copied ? Promise.resolve() : Promise.reject(new Error('Could not copy. Allow clipboard access and try again.'));
}
async function request(path, options = {}) {
  const token = localStorage.getItem('adminToken');
  if (!token) {
    const error = new Error('Your admin session has expired. Sign in again.');
    error.status = 401;
    throw error;
  }
  const abort = new AbortController();
  const timeout = setTimeout(() => abort.abort(), options.timeout || 45000);
  try {
    const response = await fetch(ROOT + path, {
      method: options.method || 'GET',
      signal: abort.signal,
      headers: {
        Authorization: `Bearer ${token}`,
        ...(options.body ? {
          'Content-Type': 'application/json'
        } : {})
      },
      ...(options.body ? {
        body: JSON.stringify(options.body)
      } : {})
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const error = new Error(data.error || data.message || `Request failed (${response.status}).`);
      error.status = response.status;
      throw error;
    }
    return data;
  } catch (error) {
    if (error.name === 'AbortError') throw new Error('The request timed out. Check its status before retrying.');
    throw error;
  } finally {
    clearTimeout(timeout);
  }
}
export default function AdminColdOutreach() {
  const [windowWidth, setWindowWidth] = useState(() => typeof window === 'undefined' ? 1200 : window.innerWidth);
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const isNarrow = windowWidth <= 1100;
  const isMobile = windowWidth <= 760;
  const [accounts, setAccounts] = useState(() => readSaved('accounts', []));
  const [account, setAccount] = useState(() => readSaved('account', ''));
  const [templates, setTemplates] = useState(() => readSaved('templates', {}));
  const [linkMode, setLinkMode] = useState(() => readSaved('linkMode', 'dm'));
  const [newAccount, setNewAccount] = useState('');
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState(EMPTY_STATS);
  const [view, setView] = useState('needs_human');
  const [source, setSource] = useState('');
  const [aiMethod, setAiMethod] = useState('all');
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [lastSend, setLastSend] = useState(() => readSaved('lastSend', null, true));
  const [pending, setPending] = useState(() => readSaved('pending', null, true));
  const [importOpen, setImportOpen] = useState(false);
  const [importType, setImportType] = useState('followers');
  const [files, setFiles] = useState([]);
  const [importSource, setImportSource] = useState('');
  const [importAccount, setImportAccount] = useState('');
  const [senderNames, setSenderNames] = useState('');
  const [headers, setHeaders] = useState([]);
  const [usernameColumn, setUsernameColumn] = useState('');
  const [importResult, setImportResult] = useState(null);
  const [importError, setImportError] = useState('');
  const [importing, setImporting] = useState(false);
  const [readingFile, setReadingFile] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [detail, setDetail] = useState(null);
  const dialog = useRef(null);
  const detailDialog = useRef(null);
  const listVersion = useRef(0);
  const refreshRef = useRef(null);
  const busyRef = useRef(false);
  const fileVersion = useRef(0);
  const message = templates[account] ?? DEFAULT_MESSAGE;
  const sentToday = stats.daily.find(item => item.account === account)?.sent || 0;
  const viewConfig = VIEWS[view] || VIEWS.needs_human;
  const bucket = viewConfig.bucket;
  const aiStats = stats.ai || {};
  const bucketStats = stats.buckets || {};
  const countByView = {
    needs_human: bucketStats.needsHuman,
    ai_rejected: bucketStats.aiRejected,
    ready: bucketStats.ready,
    contacted: bucketStats.contacted,
    manual_not_fit: bucketStats.manualNotFit,
    awaiting_ai: bucketStats.awaitingAi,
    ai_errors: bucketStats.aiErrors,
    all: bucketStats.all
  };
  const count = countByView[view];
  const decidedByAi = (Number(aiStats.autoApproved) || 0) + (Number(aiStats.autoRejected) || 0);
  const aiApprovalRate = decidedByAi > 0 ? (Number(aiStats.autoApproved) || 0) / decidedByAi : null;
  useEffect(() => {
    save('accounts', accounts);
  }, [accounts]);
  useEffect(() => {
    save('account', account);
  }, [account]);
  useEffect(() => {
    save('templates', templates);
  }, [templates]);
  useEffect(() => {
    save('linkMode', linkMode);
  }, [linkMode]);
  useEffect(() => {
    save('lastSend', lastSend, true);
  }, [lastSend]);
  useEffect(() => {
    save('pending', pending, true);
  }, [pending]);
  useEffect(() => {
    const timer = setTimeout(() => setQuery(search.trim()), 250);
    return () => clearTimeout(timer);
  }, [search]);
  const refresh = useCallback(async (after = null, quiet = false) => {
    const version = ++listVersion.current;
    if (!quiet) setLoading(true);
    try {
      const params = new URLSearchParams({
        bucket,
        limit: '50'
      });
      if (view === 'ai_rejected' && aiMethod !== 'all') params.set('aiMethod', aiMethod);
      if (source) params.set('source', source);
      if (query) params.set('q', query);
      if (after) params.set('after', after);
      const [list, nextStats] = await Promise.all([request(`/contacts?${params}`), request('/stats?timeZone=America%2FLos_Angeles')]);
      if (version !== listVersion.current) return;
      if (!Array.isArray(list.contacts) || !Array.isArray(nextStats.daily)) throw new Error('Unexpected server response. Check the backend URL.');
      setContacts(previous => after ? [...previous, ...list.contacts.filter(item => !previous.some(old => old._id === item._id))] : list.contacts);
      setCursor(list.nextCursor);
      setStats(nextStats);
    } catch (err) {
      if (version === listVersion.current) setError(err.message);
    } finally {
      if (version === listVersion.current) setLoading(false);
    }
  }, [bucket, view, aiMethod, source, query]);
  refreshRef.current = refresh;
  useEffect(() => {
    setContacts([]);
    setCursor(null);
    refresh();
  }, [refresh]);
  useEffect(() => {
    const onVisible = () => {
      if (!document.hidden && !busyRef.current) refreshRef.current(null, true);
    };
    const timer = setInterval(onVisible, 30000);
    window.addEventListener('focus', onVisible);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(timer);
      window.removeEventListener('focus', onVisible);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);
  useEffect(() => {
    if (importOpen) {
      dialog.current?.showModal();
      setImportAccount(account);
    } else dialog.current?.close();
    // Account is captured when opening; changing the main selector does not
    // silently change which account an in-progress chat import belongs to.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [importOpen]);
  useEffect(() => {
    if (detail) detailDialog.current?.showModal();else detailDialog.current?.close();
  }, [detail]);
  function addAccount(event) {
    event.preventDefault();
    const handle = normalizeHandle(newAccount);
    if (!validHandle(handle)) {
      setError('Enter a valid Instagram username.');
      return;
    }
    setAccounts(previous => [...new Set([...previous, handle])]);
    setAccount(handle);
    setNewAccount('');
    setError('');
  }
  function rememberSend(contact) {
    const current = contact.activeSend;
    if (!current) return;
    setLastSend({
      contactId: contact._id,
      username: contact.username,
      ...current
    });
  }
  async function sendDM(contact) {
    if (busyRef.current || pending) return;
    if (!validHandle(account)) {
      setError('Choose the Instagram account you are sending from.');
      return;
    }
    if (!message.trim()) {
      setError('Add your invitation message first.');
      return;
    }
    let popup;
    let submitted = false;
    let recorded = false;
    const operation = {
      contactId: contact._id,
      username: contact.username,
      account,
      operationId: crypto.randomUUID(),
      ...(source ? {
        source
      } : {})
    };
    busyRef.current = true;
    setBusy(contact._id);
    setError('');
    setNotice('');
    try {
      // Start clipboard access while the dashboard still has focus. Open the
      // blank tab synchronously so the browser sees a direct user gesture.
      const copying = copyMessage(message).then(() => null, err => err);
      popup = window.open('', '_blank');
      if (!popup) {
        await copying;
        throw new Error('Allow pop-ups for this dashboard, then try again. Nobody was marked Sent.');
      }
      popup.opener = null;
      const copyError = await copying;
      if (copyError) throw new Error('Could not copy your message. Allow clipboard access and try again. Nobody was marked Sent.');
      if (popup.closed) throw new Error('The Instagram tab was closed. Nobody was marked Sent.');
      // Persist before the request: closing/reloading this tab must not lose
      // an operation whose server response has not yet arrived.
      save('pending', operation, true);
      setPending(operation);
      submitted = true;
      const result = await request(`/contacts/${contact._id}/send`, {
        method: 'POST',
        body: operation
      });
      recorded = true;
      rememberSend(result.contact);
      setPending(null);
      save('pending', null, true);
      if (result.replayed) {
        popup.close();
        setNotice('This click was already recorded. Check the existing conversation before sending again.');
      } else if (popup.closed) {
        setNotice('The tab closed after the click was recorded. Use Undo below.');
      } else {
        popup.location.href = linkMode === 'profile' ? `https://www.instagram.com/${encodeURIComponent(result.contact.username)}/` : `https://ig.me/m/${encodeURIComponent(result.contact.username)}`;
        setNotice('Message copied. Paste and send on Instagram.');
      }
      await refreshRef.current(null, true);
    } catch (err) {
      try {
        popup?.close();
      } catch {/* It may already be closed. */}
      if (submitted && !recorded && (!err.status || err.status >= 500)) {
        setError('Could not confirm the DM click. Use “Check last click” before continuing.');
      } else {
        setPending(null);
        save('pending', null, true);
        setError(recorded ? 'The click was recorded, but Instagram did not open. Use Undo or the profile link below.' : err.message);
        if (err.status === 409) await refreshRef.current(null, true);
      }
    } finally {
      busyRef.current = false;
      setBusy('');
    }
  }
  async function checkPending() {
    if (!pending || busyRef.current) return;
    busyRef.current = true;
    setBusy('pending');
    try {
      const result = await request(`/contacts/${pending.contactId}`);
      const recorded = result.contact.history?.find(item => item.operationId === pending.operationId);
      if (!recorded && result.contact.status === 'new') {
        // Keep the SAME operation ID if the previous request is still arriving.
        // Replaying it resolves the race without a second send or new window.
        const replay = await request(`/contacts/${pending.contactId}/send`, {
          method: 'POST',
          body: pending
        });
        rememberSend(replay.contact);
        setNotice('Recovered the click. Open the profile below to finish manually, or Undo to return them to New.');
      } else {
        if (result.contact.activeSend?.operationId === pending.operationId) rememberSend(result.contact);
        setNotice(recorded?.outcome === 'sent' ? 'The click was recorded. Open their profile to finish, or Undo.' : 'Contact status recovered. The queue has been refreshed.');
      }
      setPending(null);
      save('pending', null, true);
      setError('');
      await refreshRef.current(null, true);
    } catch (err) {
      if (err.status === 409 || err.status === 404) {
        setPending(null);
        save('pending', null, true);
        await refreshRef.current(null, true);
      }
      setError(err.message);
    } finally {
      busyRef.current = false;
      setBusy('');
    }
  }
  async function correct(send, action) {
    if (!send || busyRef.current) return;
    busyRef.current = true;
    setBusy(send.contactId);
    setError('');
    try {
      await request(`/contacts/${send.contactId}/correct`, {
        method: 'POST',
        body: {
          account: send.account,
          operationId: send.operationId,
          action
        }
      });
      if (lastSend?.operationId === send.operationId) setLastSend(null);
      setNotice(action === 'undo' ? 'Click undone. Previous chat history, if any, is preserved.' : 'Marked “Couldn’t send.”');
      setDetail(null);
      await refreshRef.current(null, true);
    } catch (err) {
      setError(err.message);
    } finally {
      busyRef.current = false;
      setBusy('');
    }
  }
  async function changeStatus(contact, nextStatus) {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(contact._id);
    setError('');
    try {
      await request(`/contacts/${contact._id}/status`, {
        method: 'PATCH',
        body: {
          status: nextStatus
        }
      });
      await refreshRef.current(null, true);
    } catch (err) {
      setError(err.message);
    } finally {
      busyRef.current = false;
      setBusy('');
    }
  }

  async function changeReview(contact, fitStatus) {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(contact._id);
    setError('');
    setNotice('');
    try {
      await request(`/contacts/${contact._id}/status`, {
        method: 'PATCH',
        body: {
          fitStatus,
          status: fitStatus === 'good_fit' ? 'new' : 'skipped'
        }
      });
      setNotice(
        fitStatus === 'good_fit'
          ? `@${contact.username} moved to Ready to send.`
          : `@${contact.username} marked Not a fit.`
      );
      setDetail(null);
      await refreshRef.current(null, true);
    } catch (err) {
      setError(err.message);
    } finally {
      busyRef.current = false;
      setBusy('');
    }
  }

  async function retryAiReview(contact) {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(contact._id);
    setError('');
    setNotice('');
    try {
      await request(`/contacts/${contact._id}/status`, {
        method: 'PATCH',
        body: {
          fitStatus: 'unreviewed',
          status: 'new',
          resetAiReview: true
        }
      });
      setNotice(`@${contact.username} returned to the AI review queue.`);
      setDetail(null);
      await refreshRef.current(null, true);
    } catch (err) {
      setError(err.message);
    } finally {
      busyRef.current = false;
      setBusy('');
    }
  }

  async function showHistory(contact) {
    try {
      const result = await request(`/contacts/${contact._id}`);
      setDetail(result.contact);
    } catch (err) {
      setError(err.message);
    }
  }
  async function selectFiles(fileList) {
    const version = ++fileVersion.current;
    setReadingFile(true);
    setImportError('');
    setImportResult(null);
    setHeaders([]);
    setUsernameColumn('');
    setFiles([]);
    try {
      const selected = Array.from(fileList || []);
      if (!selected.length) return;
      if (selected.some(file => !/\.(csv|json)$/i.test(file.name))) throw new Error('Choose a CSV or JSON export. Extract ZIP archives first.');
if (
  selected.reduce((sum, file) => sum + file.size, 0) >
  75 * 1024 * 1024
) {
  throw new Error(
    'Choose files totaling under 75 MB per import.'
  );
}

if (selected.length > 1 && (importType === 'followers' || selected.some(file => /\.csv$/i.test(file.name)))) throw new Error('Import one follower CSV at a time. Chat JSON files can be selected together.');
      const loaded = await Promise.all(selected.map(async file => ({
        name: file.name,
        text: await file.text()
      })));
      if (version !== fileVersion.current) return;
      setFiles(loaded);
      setImportSource(importType === 'followers' ? detectSource(loaded[0].name) : '');
      if (/\.csv$/i.test(loaded[0].name)) {
        const columns = csvHeaders(loaded[0].text);
        setHeaders(columns);
        const recognized = columns.find(name => ['username', 'usernames', 'handle', 'profileurl', 'instagramurl'].includes(name.toLowerCase().replace(/[^a-z0-9]/g, '')));
        setUsernameColumn(recognized || '');
      }
    } catch (err) {
      if (version === fileVersion.current) setImportError(err.message);
    } finally {
      if (version === fileVersion.current) setReadingFile(false);
    }
  }
  async function runImport(event) {
    event.preventDefault();
    if (importing || !files.length || readingFile) return;
    setImporting(true);
    setImportError('');
    setImportResult(null);
    try {
      const body = {
        filename: files[0].name
      };
      if (importType === 'followers') {
        if (!validHandle(normalizeHandle(importSource))) throw new Error('Enter the page these followers came from.');
        body.source = normalizeHandle(importSource);
      } else {
        if (!validHandle(normalizeHandle(importAccount))) throw new Error('Choose the account these chats belong to.');
        body.account = normalizeHandle(importAccount);
        body.senderNames = senderNames.split('\n').map(name => name.trim()).filter(Boolean);
      }
      if (/\.csv$/i.test(files[0].name)) {
        if (!usernameColumn) throw new Error('Select the column containing usernames or profile URLs.');
        body.csv = files[0].text;
        body.columnMap = {
          username: usernameColumn
        };
      } else {
        const values = files.map(file => JSON.parse(file.text.replace(/^\uFEFF/, '')));
        if (importType === 'chats' && values.every(value => value && Array.isArray(value.messages))) body.threads = values;else if (values.length === 1 && Array.isArray(values[0])) body.contacts = values[0];else if (values.length === 1 && Array.isArray(values[0]?.contacts)) body.contacts = values[0].contacts;else throw new Error('Use a contacts array or individual message JSON files. This export format is not recognized.');
      }
    // JSON escaping can expand the CSV beyond its raw file size.
// Backend accepts request bodies up to 100 MB.
if (
  new Blob([JSON.stringify(body)]).size >
  90 * 1024 * 1024
) {
  throw new Error(
    'This import is too large. Keep the request under 90 MB.'
  );
}
      const result = await request(`/import/${importType}`, {
        method: 'POST',
        body,
        timeout: 600000
      });
      setImportResult(result);
      await refreshRef.current(null, true);
    } catch (err) {
      setImportError(err instanceof SyntaxError ? 'This JSON file could not be read.' : err.message);
    } finally {
      setImporting(false);
    }
  }
  function closeImport() {
    if (!importing) {
      setImportOpen(false);
      setDragging(false);
    }
  }
  function switchImport(type) {
    fileVersion.current += 1;
    setImportType(type);
    setFiles([]);
    setHeaders([]);
    setImportSource('');
    setImportResult(null);
    setImportError('');
    setReadingFile(false);
  }
  return <main style={{
    ...styles.page,
    ...(isMobile ? styles.pageMobile : {})
  }}>
      <header style={{
      ...styles.header,
      ...(isMobile ? styles.headerMobile : {})
    }}>
        <div style={styles.div}><h1 style={{
          ...styles.h1,
          ...(isMobile ? styles.h1Mobile : {})
        }}>Cold outreach</h1><p style={{
          ...styles.p,
          ...(isMobile ? styles.pMobile : {})
        }}>One contact list across your Instagram accounts.</p></div>
        <button onClick={() => {
        setImportResult(null);
        setImportError('');
        setImportOpen(true);
      }} style={{
        ...styles.button,
        ...(isMobile ? styles.buttonMobile : {})
      }}>Import contacts</button>
      </header>
      {error && <div role="alert" style={{
      ...styles.alert,
      ...(isMobile ? styles.alertMobile : {})
    }}><span style={styles.div}>{error}</span><button onClick={() => setError('')} aria-label="Dismiss error" style={styles.button2}>×</button></div>}
      {notice && <div role="status" style={{
      ...styles.alert2,
      ...(isMobile ? styles.alertMobile : {})
    }}><span style={styles.div}>{notice}</span><button onClick={() => setNotice('')} aria-label="Dismiss notification" style={styles.button2}>×</button></div>}
      {pending && <div style={{
      ...styles.alert3,
      ...(isMobile ? styles.alertMobile : {})
    }}><span style={styles.div}>An earlier DM click needs checking before you continue.</span><button disabled={Boolean(busy)} onClick={checkPending} style={{
        ...styles.button3,
        ...(Boolean(busy) ? {
          cursor: 'not-allowed',
          opacity: 0.45
        } : {})
      }}>Check last click</button></div>}
      <div style={{
        ...styles.metrics,
        ...(isMobile ? styles.metricsMobile : {})
      }}>
        {[
          ['needs_human', 'Needs human', bucketStats.needsHuman],
          ['ai_rejected', 'AI rejected', bucketStats.aiRejected],
          ['ready', 'Ready to send', bucketStats.ready],
          ['contacted', 'Contacted', bucketStats.contacted],
          ['awaiting_ai', 'Awaiting AI', bucketStats.awaitingAi]
        ].map(([key, label, value]) => <button
          type="button"
          key={key}
          onClick={() => setView(key)}
          style={{
            ...styles.metricButton,
            ...(view === key ? styles.metricButtonActive : {}),
            ...(isMobile ? styles.metricMobile : {})
          }}
        >
          <span style={styles.span}>{label}</span>
          <strong style={{
            ...styles.strong,
            ...(isMobile ? styles.strongMobile : {})
          }}>{formatCount(value)}</strong>
        </button>)}
      </div>

      <div style={{
        ...styles.aiSummary,
        ...(isMobile ? styles.aiSummaryMobile : {})
      }}>
        <div style={styles.aiSummaryLead}>
          <div style={styles.div}>
            <strong style={styles.aiSummaryTitle}>AI review control center</strong>
            <span style={styles.aiSummaryText}>
              {formatCount(aiStats.analyzed)} profiles analyzed
              {Number.isFinite(aiApprovalRate) ? ` · ${Math.round(aiApprovalRate * 100)}% of automatic decisions approved` : ''}
            </span>
          </div>
          <button type="button" onClick={() => setView('needs_human')} style={styles.auditButton}>
            Review uncertain profiles
          </button>
        </div>

        <div style={{
          ...styles.aiSummaryStats,
          ...(isMobile ? styles.aiSummaryStatsMobile : {})
        }}>
          <button type="button" onClick={() => setView('ready')} style={styles.aiStatButton}>
            <b style={styles.b}>{formatCount(aiStats.autoApproved)}</b>
            <span style={styles.aiStatLabel}>AI approved</span>
          </button>
          <button type="button" onClick={() => {
            setAiMethod('vision');
            setView('ai_rejected');
          }} style={styles.aiStatButton}>
            <b style={styles.b}>{formatCount(aiStats.visionRejected)}</b>
            <span style={styles.aiStatLabel}>Vision rejected</span>
          </button>
          <button type="button" onClick={() => {
            setAiMethod('prepass');
            setView('ai_rejected');
          }} style={styles.aiStatButton}>
            <b style={styles.b}>{formatCount(aiStats.prepassRejected)}</b>
            <span style={styles.aiStatLabel}>Prepass rejected</span>
          </button>
          <button type="button" onClick={() => setView('needs_human')} style={styles.aiStatButton}>
            <b style={styles.b}>{formatCount(aiStats.needsHuman)}</b>
            <span style={styles.aiStatLabel}>Needs human</span>
          </button>
          <button type="button" onClick={() => setView('ai_errors')} style={styles.aiStatButton}>
            <b style={styles.b}>{formatCount(aiStats.errors)}</b>
            <span style={styles.aiStatLabel}>Errors</span>
          </button>
        </div>

        {(Number(aiStats.autoApproved) || Number(aiStats.autoRejected) || Number(aiStats.needsHuman)) > 0 && <div style={styles.outcomeTrack} aria-label="AI outcome mix">
          <div title="Approved" style={{
            ...styles.outcomeApproved,
            width: `${((Number(aiStats.autoApproved) || 0) / Math.max(1, (Number(aiStats.autoApproved) || 0) + (Number(aiStats.autoRejected) || 0) + (Number(aiStats.needsHuman) || 0))) * 100}%`
          }} />
          <div title="Needs human" style={{
            ...styles.outcomeHuman,
            width: `${((Number(aiStats.needsHuman) || 0) / Math.max(1, (Number(aiStats.autoApproved) || 0) + (Number(aiStats.autoRejected) || 0) + (Number(aiStats.needsHuman) || 0))) * 100}%`
          }} />
          <div title="Rejected" style={{
            ...styles.outcomeRejected,
            width: `${((Number(aiStats.autoRejected) || 0) / Math.max(1, (Number(aiStats.autoApproved) || 0) + (Number(aiStats.autoRejected) || 0) + (Number(aiStats.needsHuman) || 0))) * 100}%`
          }} />
        </div>}

        <div style={styles.aiPipeline}>
          <span style={styles.secondary}>{formatCount(aiStats.queued)} queued</span>
          <span style={styles.secondary}>{formatCount(aiStats.processing)} processing</span>
          <span style={styles.secondary}>{formatCount(aiStats.overrides)} AI decisions overridden</span>
          <span style={styles.secondary}>{formatCount(bucketStats.manualNotFit)} manual rejections</span>
        </div>
      </div>

      <div style={{
      ...styles.layout,
      ...(isNarrow ? styles.layoutNarrow : {}),
      ...(isMobile ? styles.layoutMobile : {})
    }}>
        <aside style={styles.div}>
          <section style={{
          ...styles.panel,
          ...(isMobile ? styles.panelMobile : {})
        }}>
            <h2 style={styles.h2}>Sending from</h2>
            <label htmlFor="co-account" style={styles.label}>Instagram account</label>
            <select id="co-account" value={account} onChange={event => setAccount(event.target.value)} disabled={Boolean(busy)} style={styles.select}>
              <option value="" style={styles.div}>Choose an account</option>
              {[...new Set([...accounts, ...(account ? [account] : [])])].map(handle => <option key={handle} value={handle} style={styles.div}>@{handle}</option>)}
            </select>
            <p style={styles.hint}>Match the account logged into Instagram in this Chrome profile.{account ? ` ${sentToday.toLocaleString()} sent today from this account.` : ''}</p>
            <details open={!accounts.length} style={styles.div}><summary style={styles.summary}>Add an account</summary><form onSubmit={addAccount} style={styles.inlineForm}><input aria-label="New sending account" placeholder="@username" value={newAccount} onChange={event => setNewAccount(event.target.value)} maxLength={31} style={styles.input} /><button disabled={Boolean(busy)} style={{
                ...styles.button3,
                ...(Boolean(busy) ? {
                  cursor: 'not-allowed',
                  opacity: 0.45
                } : {})
              }}>Add</button></form></details>
            <div style={styles.divider} />
            <label htmlFor="co-message" style={styles.label}>Invitation message</label>
            <textarea id="co-message" rows={8} value={message} disabled={!account} onChange={event => setTemplates(previous => ({
            ...previous,
            [account]: event.target.value
          }))} style={{
            ...styles.textarea,
            ...(isMobile ? styles.textareaMobile : {}),
            ...(!account ? {
              background: '#f6f6f6',
              color: '#888'
            } : {})
          }} />
            <p style={styles.hint}>Copied when you click DM. Saved for this account in this browser.</p>
            <label htmlFor="co-link-mode" style={styles.label}>Open Instagram to</label>
            <select id="co-link-mode" value={linkMode} onChange={event => setLinkMode(event.target.value)} style={styles.select}><option value="dm" style={styles.div}>Messages</option><option value="profile" style={styles.div}>Profile</option></select>
            <p style={styles.hint}>Use Profile if the message link doesn’t open correctly.</p>
          </section>
          <details style={{
          ...styles.panel,
          ...(isMobile ? styles.panelMobile2 : {})
        }}><summary style={styles.summary}>Source pages <span style={styles.span2}>{stats.sources.length}</span></summary>
            {!stats.sources.length && <p style={styles.hint}>Your source pages appear after an import.</p>}
            {stats.sources.map(item => <button key={item.source} onClick={() => setSource(source === item.source ? '' : item.source)} style={{
            ...styles.source,
            ...(source === item.source ? {
              background: '#f3f3f3'
            } : {})
          }}><strong style={styles.strong2}>@{item.source}</strong><span style={styles.span3}>{item.remaining.toLocaleString()} remaining · {item.sendsAttributed.toLocaleString()} sent from this list</span></button>)}
          </details>
        </aside>
        <section style={styles.workspace}>
          <div style={{
          ...styles.toolbar,
          ...(isMobile ? styles.toolbarMobile : {})
        }}>
            <div style={styles.div}><h2 style={styles.h22}>{viewConfig.label}</h2><p style={styles.p}>{viewConfig.description}</p></div>
            <button onClick={() => {
            setError('');
            refresh();
          }} disabled={loading || Boolean(busy)} style={{
            ...styles.button3,
            ...(loading || Boolean(busy) ? {
              cursor: 'not-allowed',
              opacity: 0.45
            } : {})
          }}>Refresh</button>
          </div>
          <div style={{
          ...styles.filters,
          ...(isNarrow ? styles.filtersNarrow : {}),
          ...(isMobile ? styles.filtersMobile : {})
        }}>
            <input aria-label="Search contacts" placeholder="Search name or username" value={search} onChange={event => setSearch(event.target.value)} maxLength={100} style={{
            ...styles.select,
            ...(isNarrow ? styles.inputNarrow : {})
          }} />
            <select aria-label="Outreach queue" value={view} onChange={event => setView(event.target.value)} style={styles.select}>{Object.entries(VIEWS).map(([value, config]) => <option value={value} key={value} style={styles.div}>{config.label}</option>)}</select>
            <select aria-label="Source page" value={source} onChange={event => setSource(event.target.value)} style={styles.select}><option value="" style={styles.div}>All source pages</option>{stats.sources.map(item => <option value={item.source} key={item.source} style={styles.div}>@{item.source}</option>)}</select>
          </div>
          {view === 'ai_rejected' && <div style={styles.aiMethodBar}>
            <span style={styles.aiMethodLabel}>AI rejection type</span>
            {[
              ['all', 'All'],
              ['vision', 'Vision AI'],
              ['prepass', 'Fast prepass']
            ].map(([value, label]) => <button
              type="button"
              key={value}
              onClick={() => setAiMethod(value)}
              style={{
                ...styles.methodButton,
                ...(aiMethod === value ? styles.methodButtonActive : {})
              }}
            >{label}</button>)}
            <span style={styles.aiMethodHint}>
              Vision reviewed Instagram content. Fast prepass used only username/full name.
            </span>
          </div>}
          {lastSend && <div style={{
          ...styles.lastSend,
          ...(isNarrow ? styles.lastSendNarrow : {}),
          ...(isMobile ? styles.lastSendMobile : {})
        }}><div style={styles.div}><strong style={styles.strong3}>Last click: @{lastSend.username}</strong><span style={styles.span4}>From @{lastSend.account} · {dateLabel(lastSend.recordedAt)}</span></div><div style={styles.actions}><a href={`https://www.instagram.com/${encodeURIComponent(lastSend.username)}/`} target="_blank" rel="noopener noreferrer" style={styles.a}>Profile</a><button onClick={() => correct(lastSend, 'undo')} disabled={Boolean(busy)} style={{
              ...styles.button4,
              ...(Boolean(busy) ? {
                cursor: 'not-allowed',
                opacity: 0.45
              } : {})
            }}>Undo</button><button onClick={() => correct(lastSend, 'couldnt_send')} disabled={Boolean(busy)} style={{
              ...styles.button4,
              ...(Boolean(busy) ? {
                cursor: 'not-allowed',
                opacity: 0.45
              } : {})
            }}>Couldn’t send</button></div></div>}
          <div aria-busy={loading} style={styles.tableWrap}>
            <table style={{
              ...styles.table,
              ...(isMobile ? styles.tableMobile : {})
            }}>
              <thead style={{
                ...styles.div,
                ...(isMobile ? styles.theadMobile : {})
              }}>
                <tr style={{
                  ...styles.div,
                  ...(isMobile ? styles.trMobile : {})
                }}>
                  <th style={{
                    ...styles.th,
                    ...(isNarrow ? styles.thNarrow : {})
                  }}>Person</th>
                  <th style={{
                    ...styles.th2,
                    ...(isNarrow ? styles.thNarrow : {})
                  }}>Decision</th>
                  <th style={{
                    ...styles.th2,
                    ...(isNarrow ? styles.thNarrow : {})
                  }}>AI assessment</th>
                  <th style={{
                    ...styles.th2,
                    ...(isNarrow ? styles.thNarrow : {})
                  }}>Source</th>
                  <th style={{
                    ...styles.th2,
                    ...(isNarrow ? styles.thNarrow : {})
                  }}><span style={styles.srOnly}>Actions</span></th>
                </tr>
              </thead>
              <tbody style={{
                ...styles.div,
                ...(isMobile ? styles.tableMobile : {})
              }}>
                {contacts.map(contact => {
                  const state = reviewState(contact);
                  const effectiveFitStatus = contact.fitStatus || 'unreviewed';
                  const reason = topAiReason(contact.aiReview);
                  const isAiRejected =
                    effectiveFitStatus === 'not_fit' &&
                    contact.aiReview?.decision === 'not_fit' &&
                    contact.aiReview?.autoApplied;
                  const needsHuman =
                    effectiveFitStatus === 'unreviewed' &&
                    Boolean(contact.aiReview?.analyzedAt);
                  const isAiError =
                    effectiveFitStatus === 'unreviewed' &&
                    contact.aiReviewStatus === 'error';
                  const isReady =
                    effectiveFitStatus === 'good_fit' &&
                    contact.status === 'new';
                  const isManualNotFit =
                    effectiveFitStatus === 'not_fit' &&
                    !isAiRejected;

                  const assessmentDetail = contact.aiReview
                    ? contact.aiReview.model === 'fast-prepass-v1'
                      ? 'Username + full-name screening'
                      : [
                          contact.aiReview.estimatedAgeRange ? `Age ${contact.aiReview.estimatedAgeRange}` : null,
                          Number.isFinite(contact.aiReview.styleFit) ? `Style ${percentLabel(contact.aiReview.styleFit)}` : null,
                          Number.isFinite(contact.aiReview.socialFit) ? `Social ${percentLabel(contact.aiReview.socialFit)}` : null,
                          Number.isFinite(contact.aiReview.visualPresentation) ? `Visual ${percentLabel(contact.aiReview.visualPresentation)}` : null
                        ].filter(Boolean).join(' · ')
                    : contact.aiReviewStatus === 'processing'
                      ? 'AI is processing this profile'
                      : contact.aiReviewStatus === 'queued'
                        ? 'Snapshot queued for AI'
                        : contact.aiReviewStatus === 'error'
                          ? contact.aiReviewError || 'AI review failed'
                          : 'Not analyzed yet';

                  return <tr key={contact._id} style={{
                    ...styles.div,
                    ...(isMobile ? styles.trMobile : {})
                  }}>
                    <td style={{
                      ...styles.td,
                      ...(isNarrow ? styles.thNarrow : {}),
                      ...(needsHuman ? styles.reviewTd : {}),
                      ...(isMobile ? styles.tdMobile : {})
                    }}>
                      <div style={{
                        ...styles.personCell,
                        ...(needsHuman ? styles.reviewPersonCell : {})
                      }}>
                        <a
                          href={`https://www.instagram.com/${encodeURIComponent(contact.username)}/`}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={`Open @${contact.username} on Instagram`}
                          style={styles.avatarLink}
                        >
                          {contact.profilePictureUrl
                            ? <img src={contact.profilePictureUrl} alt={`@${contact.username}`} loading="lazy" referrerPolicy="no-referrer" style={{
                                ...styles.avatar,
                                ...(needsHuman ? styles.reviewAvatar : {}),
                                ...(needsHuman && isMobile ? styles.reviewAvatarMobile : {})
                              }} />
                            : <span style={{
                                ...styles.avatarFallback,
                                ...(needsHuman ? styles.reviewAvatarFallback : {}),
                                ...(needsHuman && isMobile ? styles.reviewAvatarMobile : {})
                              }}>{initials(contact)}</span>}
                        </a>
                        <div style={styles.personText}>
                          <a href={`https://www.instagram.com/${encodeURIComponent(contact.username)}/`} target="_blank" rel="noopener noreferrer" style={styles.person}>@{contact.username}</a>
                          <span style={styles.secondary}>{contact.fullName || '—'}</span>
                          {(Number.isFinite(contact.followerCount) || Number.isFinite(contact.followingCount)) && <span style={styles.secondary}>
                            {Number.isFinite(contact.followerCount) ? `${contact.followerCount.toLocaleString()} followers` : ''}
                            {Number.isFinite(contact.followerCount) && Number.isFinite(contact.followingCount) ? ' · ' : ''}
                            {Number.isFinite(contact.followingCount) ? `${contact.followingCount.toLocaleString()} following` : ''}
                          </span>}
                        </div>
                      </div>
                    </td>

                    <td style={{
                      ...styles.td2,
                      ...(isNarrow ? styles.thNarrow : {}),
                      ...(isMobile ? styles.tdMobile2 : {})
                    }}>
                      <button type="button" onClick={() => showHistory(contact)} style={{
                        ...styles.decisionBadge,
                        ...(state.kind === 'approved' ? styles.decisionApproved : {}),
                        ...(state.kind === 'rejected' ? styles.decisionRejected : {}),
                        ...(state.kind === 'human' ? styles.decisionHuman : {}),
                        ...(state.kind === 'error' ? styles.decisionError : {}),
                        ...(state.kind === 'pending' ? styles.decisionPending : {})
                      }}>{state.label}</button>
                      <span style={styles.secondary}>{state.detail}</span>
                      {contact.reviewedAt && <span style={styles.secondary}>{dateLabel(contact.reviewedAt)}</span>}
                    </td>

                    <td style={{
                      ...styles.td2,
                      ...(isNarrow ? styles.thNarrow : {}),
                      ...(isMobile ? styles.tdMobile3 : {})
                    }}>
                      <span style={styles.aiReason}>{reason || assessmentDetail}</span>
                      {reason && assessmentDetail && <span style={styles.secondary}>{assessmentDetail}</span>}
                      {contact.aiReview?.genderAssessment && <span style={styles.secondary}>
                        {contact.aiReview.genderAssessment === 'woman' ? 'Woman' : contact.aiReview.genderAssessment === 'man' ? 'Man' : 'Gender unclear'}
                        {contact.aiReview.locationAssessment ? ` · ${contact.aiReview.locationAssessment}` : ''}
                      </span>}
                    </td>

                    <td style={{
                      ...styles.td2,
                      ...(isNarrow ? styles.thNarrow : {}),
                      ...(isMobile ? styles.tdMobile4 : {})
                    }}>
                      <span style={styles.sourceName}>{contact.sources?.[0] ? `@${contact.sources[0]}` : 'Chat import'}</span>
                      {contact.sources?.length > 1 && <span title={contact.sources.map(value => `@${value}`).join(', ')} style={styles.secondary}>+{contact.sources.length - 1} more</span>}
                      {contact.activeSend?.account && <span style={styles.secondary}>Sent by @{contact.activeSend.account}</span>}
                    </td>

                    <td style={{
                      ...styles.td3,
                      ...(isNarrow ? styles.thNarrow : {}),
                      ...(isMobile ? styles.tdMobile5 : {})
                    }}>
                      <div style={{
                        ...styles.rowActions,
                        ...(isNarrow ? styles.rowActionsNarrow : {}),
                        ...(needsHuman ? styles.reviewRowActions : {}),
                        ...(isMobile ? styles.rowActionsMobile : {})
                      }}>
                        {needsHuman ? <>
                          <button onClick={() => changeReview(contact, 'good_fit')} disabled={Boolean(busy)} style={{
                            ...styles.button,
                            ...styles.reviewActionButton,
                            ...(Boolean(busy) ? styles.disabledButton : {})
                          }}>{busy === contact._id ? 'Saving…' : 'Good fit'}</button>
                          <button onClick={() => changeReview(contact, 'not_fit')} disabled={Boolean(busy)} style={{
                            ...styles.button3,
                            ...styles.reviewActionButton,
                            ...(Boolean(busy) ? styles.disabledButton : {})
                          }}>Not a fit</button>
                          <a href={`https://www.instagram.com/${encodeURIComponent(contact.username)}/`} target="_blank" rel="noopener noreferrer" style={styles.a}>Instagram</a>
                        </> : isAiRejected ? <>
                          <button onClick={() => changeReview(contact, 'good_fit')} disabled={Boolean(busy)} style={{
                            ...styles.button,
                            ...(Boolean(busy) ? styles.disabledButton : {})
                          }}>{busy === contact._id ? 'Saving…' : 'Approve'}</button>
                          <a href={`https://www.instagram.com/${encodeURIComponent(contact.username)}/`} target="_blank" rel="noopener noreferrer" style={styles.a}>Instagram</a>
                          <button onClick={() => showHistory(contact)} style={styles.textButton}>Details</button>
                        </> : isAiError ? <>
                          <button onClick={() => retryAiReview(contact)} disabled={Boolean(busy)} style={{
                            ...styles.button,
                            ...(Boolean(busy) ? styles.disabledButton : {})
                          }}>{busy === contact._id ? 'Resetting…' : 'Retry AI'}</button>
                          <a href={`https://www.instagram.com/${encodeURIComponent(contact.username)}/`} target="_blank" rel="noopener noreferrer" style={styles.a}>Instagram</a>
                        </> : isReady ? <>
                          <button onClick={() => sendDM(contact)} disabled={Boolean(busy) || Boolean(pending) || !account} style={{
                            ...styles.button,
                            ...(Boolean(busy) || Boolean(pending) || !account ? styles.disabledButton : {})
                          }}>{busy === contact._id ? 'Opening…' : 'Copy & DM'}</button>
                          <button onClick={() => changeReview(contact, 'not_fit')} disabled={Boolean(busy)} style={{
                            ...styles.textButton,
                            ...(Boolean(busy) ? styles.disabledButton : {})
                          }}>Not a fit</button>
                        </> : isManualNotFit ? <>
                          <button onClick={() => changeReview(contact, 'good_fit')} disabled={Boolean(busy)} style={{
                            ...styles.button3,
                            ...(Boolean(busy) ? styles.disabledButton : {})
                          }}>Move to Good fit</button>
                          <a href={`https://www.instagram.com/${encodeURIComponent(contact.username)}/`} target="_blank" rel="noopener noreferrer" style={styles.a}>Instagram</a>
                        </> : contact.activeSend ? <>
                          <button onClick={() => showHistory(contact)} style={styles.textButton}>History</button>
                          <button onClick={() => correct({
                            contactId: contact._id,
                            ...contact.activeSend
                          }, 'undo')} disabled={Boolean(busy)} style={{
                            ...styles.textButton,
                            ...(Boolean(busy) ? styles.disabledButton : {})
                          }}>Undo</button>
                        </> : contact.status === 'couldnt_send' && effectiveFitStatus === 'good_fit' ? <button onClick={() => changeStatus(contact, 'new')} disabled={Boolean(busy)} style={{
                          ...styles.textButton,
                          ...(Boolean(busy) ? styles.disabledButton : {})
                        }}>Return to Ready</button> : <>
                          <a href={`https://www.instagram.com/${encodeURIComponent(contact.username)}/`} target="_blank" rel="noopener noreferrer" style={styles.a}>Instagram</a>
                          <button onClick={() => showHistory(contact)} style={styles.textButton}>Details</button>
                        </>}
                      </div>
                    </td>
                  </tr>;
                })}
              </tbody>
            </table>
            {!contacts.length && <div style={styles.empty}>
              <h3 style={styles.h3}>{loading ? 'Loading contacts…' : query || source ? 'No matching contacts' : `No ${viewConfig.label.toLowerCase()} contacts`}</h3>
              <p style={styles.p2}>
                {loading
                  ? 'Getting the shared contact list.'
                  : view === 'needs_human'
                    ? 'Nothing currently needs a human decision.'
                    : view === 'ai_rejected'
                      ? aiMethod === 'all' ? 'No active AI rejections.' : `No ${aiMethod === 'vision' ? 'Vision AI' : 'fast prepass'} rejections.`
                      : view === 'ai_errors'
                        ? 'No AI review errors.'
                        : view === 'awaiting_ai'
                          ? 'Nothing is waiting for AI review.'
                          : 'Try another queue, filter, or source page.'}
              </p>
            </div>}
          </div>
          <footer style={styles.listFooter}><span style={styles.div}>{contacts.length.toLocaleString()} loaded{!source && !query && view !== 'all' && Number.isFinite(count) ? ` of ${count.toLocaleString()}` : ''}</span>{cursor && <button disabled={loading || Boolean(busy)} onClick={() => refresh(cursor)} style={{
            ...styles.button3,
            ...(loading || Boolean(busy) ? {
              cursor: 'not-allowed',
              opacity: 0.45
            } : {})
          }}>{loading ? 'Loading…' : 'Load more'}</button>}</footer>
          <p style={styles.footnote}>AI decisions stay auditable even after you override them. “Needs human” only contains profiles AI already analyzed but could not confidently resolve. “Awaiting AI” contains profiles that have not finished AI review yet.</p>
        </section>
      </div>

      <dialog ref={dialog} onCancel={event => {
      event.preventDefault();
      closeImport();
    }} style={{
      ...styles.dialog,
      ...(isMobile ? styles.dialogMobile : {})
    }}>
        <form onSubmit={runImport} style={styles.div}>
          <div style={styles.dialogHeader}><h2 style={styles.h23}>Import contacts</h2><button type="button" onClick={closeImport} disabled={importing} aria-label="Close import" style={{
            ...styles.button5,
            ...(importing ? {
              cursor: 'not-allowed',
              opacity: 0.45
            } : {})
          }}>×</button></div>
          <div style={styles.segment}><button type="button" onClick={() => switchImport('followers')} disabled={importing} style={{
            ...styles.button6,
            ...(importType === 'followers' ? {
              background: 'white',
              color: '#111',
              boxShadow: '0 1px 4px #0001'
            } : {}),
            ...(importing ? {
              cursor: 'not-allowed',
              opacity: 0.45
            } : {})
          }}>Follower list</button><button type="button" onClick={() => switchImport('chats')} disabled={importing} style={{
            ...styles.button6,
            ...(importType === 'chats' ? {
              background: 'white',
              color: '#111',
              boxShadow: '0 1px 4px #0001'
            } : {}),
            ...(importing ? {
              cursor: 'not-allowed',
              opacity: 0.45
            } : {})
          }}>Previously contacted</button></div>
          <p style={styles.hint}>{importType === 'followers' ? 'Drop in your DolphinRadar CSV. Existing contacts keep their history.' : 'Import outgoing chat history or a list of people you have already messaged. These people will be excluded from new outreach.'}</p>
          <label onDragOver={event => {
          event.preventDefault();
          setDragging(true);
        }} onDragLeave={() => setDragging(false)} onDrop={event => {
          event.preventDefault();
          setDragging(false);
          if (!importing) selectFiles(event.dataTransfer.files);
        }} style={{
          ...styles.dropzone,
          ...(dragging ? {
            background: '#eee',
            borderColor: '#333'
          } : {})
        }}>
            <input type="file" accept=".csv,.json" multiple={importType === 'chats'} disabled={importing} onChange={event => {
            selectFiles(event.target.files);
            event.target.value = '';
          }} style={styles.input2} />
            <strong style={styles.strong4}>{readingFile ? 'Reading file…' : files.length ? files.map(file => file.name).join(', ') : 'Drop your export here'}</strong><span style={styles.span5}>{files.length ? 'Click to choose another file' : 'or click to browse · CSV or JSON'}</span>
          </label>
          {importType === 'followers' ? <label style={styles.field}>Source page<input value={importSource} onChange={event => setImportSource(event.target.value)} placeholder="@page_you_exported" disabled={importing} style={styles.input3} /><span style={styles.hint2}>Filled from the filename when available.</span></label> : <><label style={styles.field}>Sending account<input list="co-import-accounts" value={importAccount} onChange={event => setImportAccount(event.target.value)} placeholder="@account_these_chats_belong_to" disabled={importing} style={styles.input3} /><datalist id="co-import-accounts" style={styles.div}>{accounts.map(handle => <option value={handle} key={handle} style={styles.div} />)}</datalist></label>{files.some(file => /\.json$/i.test(file.name)) && <label style={styles.field}>Your name in the chat export<textarea rows={2} value={senderNames} onChange={event => setSenderNames(event.target.value)} placeholder="Exact sender name, one per line" disabled={importing} style={{
              ...styles.textarea2,
              ...(isMobile ? styles.textareaMobile : {}),
              ...(importing ? {
                background: '#f6f6f6',
                color: '#888'
              } : {})
            }} /><span style={styles.hint2}>Used to identify messages you sent. Display names without a verified recipient username are flagged for review.</span></label>}</>}
          {headers.length > 0 && <label style={styles.field}>Username column<select value={usernameColumn} onChange={event => setUsernameColumn(event.target.value)} disabled={importing} style={styles.input3}><option value="" style={styles.div}>Choose a column</option>{headers.map((header, index) => <option key={`${header}-${index}`} value={header} style={styles.div}>{header}</option>)}</select></label>}
          {importError && <p role="alert" style={styles.importError}>{importError}</p>}
          {importResult && <div role="status" style={styles.importResult}><strong style={styles.div}>Import complete</strong><div style={styles.importCounts}><span style={styles.div}><b style={styles.b}>{importResult.added}</b> new records</span><span style={styles.div}><b style={styles.b}>{importResult.existing}</b> existing records</span><span style={styles.div}><b style={styles.b}>{importResult.alreadyContacted}</b> already contacted</span><span style={styles.div}><b style={styles.b}>{importResult.duplicatesInFile}</b> duplicates in file</span></div>{importResult.ignored > 0 && <p style={styles.p3}>{importResult.ignored} records ignored.</p>}{importResult.unresolvedCount > 0 && <details open style={styles.div}><summary style={styles.summary}>{importResult.unresolvedCount} records need review</summary><p style={styles.hint}>Unmatched records were not added. Chat exports with display names alone cannot reliably identify Instagram usernames.</p><ul style={styles.ul}>{importResult.unresolved.slice(0, 50).map((item, index) => <li key={index} style={styles.div}>Row {item.row}{item.name ? ` · ${item.name}` : ''}: {item.reason}</li>)}</ul>{importResult.unresolvedCount > 50 && <p style={styles.p3}>Showing the first 50 records.</p>}</details>}</div>}
          <div style={styles.dialogFooter}><button type="button" onClick={closeImport} disabled={importing} style={{
            ...styles.button3,
            ...(importing ? {
              cursor: 'not-allowed',
              opacity: 0.45
            } : {})
          }}>{importResult ? 'Done' : 'Cancel'}</button><button disabled={importing || readingFile || !files.length} style={{
            ...styles.button,
            ...(importing || readingFile || !files.length ? {
              cursor: 'not-allowed',
              opacity: 0.45
            } : {})
          }}>{importing ? 'Importing…' : importResult ? 'Import again' : 'Import contacts'}</button></div>
        </form>
      </dialog>
      <dialog ref={detailDialog} onCancel={() => setDetail(null)} style={{
      ...styles.dialog,
      ...(isMobile ? styles.dialogMobile : {})
    }}>
        {detail && <><div style={styles.dialogHeader}><h2 style={styles.h23}>@{detail.username}</h2><button onClick={() => setDetail(null)} aria-label="Close history" style={styles.button5}>×</button></div><p style={styles.p4}>{detail.fullName}</p><h3 style={styles.h32}>Review decision</h3><p style={styles.p4}>{FIT_STATUSES[detail.fitStatus || 'unreviewed'] || 'Unreviewed'}{detail.reviewedAt ? ` · ${dateLabel(detail.reviewedAt)}` : ''}</p><h3 style={styles.h32}>AI review</h3>
          {detail.aiReview ? <>
            <div style={styles.detailDecisionRow}>
              <span style={{
                ...styles.decisionBadge,
                ...(detail.aiReview.decision === 'good_fit' ? styles.decisionApproved : {}),
                ...(detail.aiReview.decision === 'not_fit' ? styles.decisionRejected : {}),
                ...(detail.aiReview.decision === 'uncertain' ? styles.decisionHuman : {})
              }}>{aiDecisionLabel(detail.aiReview)}</span>
              <span style={styles.secondary}>{aiMethodLabel(detail.aiReview)} · {percentLabel(detail.aiReview.confidence)} confidence{detail.aiReview.autoApplied ? ' · automatically applied' : ' · left for human review'}</span>
            </div>

            {detail.aiReview.model === 'fast-prepass-v1'
              ? <p style={styles.p4}>This decision used only the username and full name. Instagram photos, bio, followers, style, and social signals were not evaluated.</p>
              : <>
                  <p style={styles.p4}>Account: {detail.aiReview.accountType} · Gender: {detail.aiReview.genderAssessment} · Age: {detail.aiReview.estimatedAgeRange || 'unclear'} ({percentLabel(detail.aiReview.ageConfidence)}) · Location: {detail.aiReview.locationAssessment || 'unclear'} ({percentLabel(detail.aiReview.locationConfidence)})</p>
                  <div style={styles.scoreGrid}>
                    {[
                      ['Visual', detail.aiReview.visualPresentation],
                      ['Style', detail.aiReview.styleFit],
                      ['Social fit', detail.aiReview.socialFit],
                      ['Account quality', detail.aiReview.accountQuality]
                    ].map(([label, value]) => <div key={label} style={styles.scoreCard}>
                      <span style={styles.secondary}>{label}</span>
                      <strong style={styles.scoreValue}>{percentLabel(value)}</strong>
                    </div>)}
                  </div>
                </>}

            {detail.aiReview.reasons?.length ? <div style={styles.reasonBox}>
              <strong style={styles.reasonTitle}>Why AI made this call</strong>
              <ul style={styles.reasonList}>{detail.aiReview.reasons.map((reason, index) => <li key={index} style={styles.reasonItem}>{reason}</li>)}</ul>
            </div> : null}

            <div style={styles.detailActions}>
              <a href={`https://www.instagram.com/${encodeURIComponent(detail.username)}/`} target="_blank" rel="noopener noreferrer" style={styles.button3}>Open Instagram</a>
              {(detail.fitStatus || 'unreviewed') === 'unreviewed' && <>
                <button onClick={() => changeReview(detail, 'good_fit')} disabled={Boolean(busy)} style={{
                  ...styles.button,
                  ...(Boolean(busy) ? styles.disabledButton : {})
                }}>Good fit</button>
                <button onClick={() => changeReview(detail, 'not_fit')} disabled={Boolean(busy)} style={{
                  ...styles.button3,
                  ...(Boolean(busy) ? styles.disabledButton : {})
                }}>Not a fit</button>
              </>}
              {(detail.fitStatus || 'unreviewed') === 'not_fit' && <button onClick={() => changeReview(detail, 'good_fit')} disabled={Boolean(busy)} style={{
                ...styles.button,
                ...(Boolean(busy) ? styles.disabledButton : {})
              }}>Approve anyway</button>}
            </div>

            <p style={styles.secondary}>Model: {detail.aiReview.model || '—'}{detail.aiReview.analyzedAt ? ` · ${dateLabel(detail.aiReview.analyzedAt)}` : ''}</p>
          </> : <p style={styles.p4}>Not analyzed by AI yet.{detail.aiReviewStatus === 'error' && detail.aiReviewError ? ` Error: ${detail.aiReviewError}` : ''}</p>}
          <h3 style={styles.h32}>Current outreach status</h3><p style={styles.p4}>{detail.status === 'sent' ? 'Contacted' : detail.status === 'new' && detail.fitStatus === 'good_fit' ? 'Ready to send' : STATUSES[detail.status] || detail.status}</p><h3 style={styles.h32}>Source pages</h3><p style={styles.p4}>{detail.sources?.length ? detail.sources.map(value => `@${value}`).join(', ') : 'No follower source recorded.'}</p><h3 style={styles.h32}>Imported chat history</h3><p style={styles.p4}>{detail.importedAccounts?.length ? detail.importedAccounts.map(value => `@${value}`).join(', ') : 'None imported.'}</p><h3 style={styles.h32}>Outreach history</h3>{!detail.history?.length && <p style={styles.p4}>No recorded outreach sends.</p>}<ul style={styles.history}>{[...(detail.history || [])].reverse().map(item => <li key={item.operationId} style={styles.li}><strong style={styles.strong2}>@{item.account}</strong><span style={styles.span6}>{dateLabel(item.recordedAt)} · {item.outcome === 'undone' ? 'Undone' : item.outcome === 'couldnt_send' ? 'Couldn’t send' : 'Sent'}{item.source ? ` · @${item.source}` : ''}</span></li>)}</ul><div style={styles.dialogFooter}><button onClick={() => setDetail(null)} style={styles.button3}>Close</button></div></>}
      </dialog>
    </main>;
}
const styles = {
  page: {
    color: "#171717",
    background: "#fafafa",
    fontFamily: "Inter,-apple-system,BlinkMacSystemFont,\"Segoe UI\",sans-serif",
    fontSize: 16,
    lineHeight: 1.5,
    minHeight: "100vh",
    padding: "32px clamp(16px,3vw,48px) 48px",
    boxSizing: "border-box"
  },
  pageMobile: {
    padding: "22px 16px 32px"
  },
  header: {
    boxSizing: "border-box",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 20,
    maxWidth: 1500,
    margin: "0 auto 28px"
  },
  headerMobile: {
    alignItems: "flex-start",
    gap: 12
  },
  div: {
    boxSizing: "border-box"
  },
  h1: {
    boxSizing: "border-box",
    margin: "9px 0 8px",
    fontSize: 34,
    letterSpacing: -1.3,
    fontWeight: 650,
    lineHeight: 1.2
  },
  h1Mobile: {
    fontSize: 28
  },
  p: {
    boxSizing: "border-box",
    margin: "0",
    color: "#666",
    fontSize: 14,
    marginTop: 4
  },
  pMobile: {
    maxWidth: 260
  },
  button: {
    boxSizing: "border-box",
    border: "1px solid #d7d7d7",
    background: "#191919",
    color: "#fff",
    borderRadius: 7,
    padding: "9px 14px",
    fontSize: 14,
    fontWeight: 550,
    lineHeight: 1.4,
    whiteSpace: "nowrap",
    fontFamily: "inherit",
    cursor: "pointer",
    borderColor: "#191919"
  },
  buttonMobile: {
    marginTop: 28
  },
  alert: {
    boxSizing: "border-box",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 14,
    maxWidth: 1500,
    padding: "12px 16px",
    margin: "0 auto 16px",
    border: "1px solid #ddd",
    borderRadius: 7,
    background: "#fff5f4",
    fontSize: 14,
    borderColor: "#eed1cc",
    color: "#8d2c22"
  },
  alertMobile: {
    flexWrap: "wrap"
  },
  button2: {
    boxSizing: "border-box",
    fontFamily: "inherit",
    cursor: "pointer",
    border: "0",
    background: "transparent",
    fontSize: 23,
    padding: "0 3px",
    color: "inherit"
  },
  alert2: {
    boxSizing: "border-box",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 14,
    maxWidth: 1500,
    padding: "12px 16px",
    margin: "0 auto 16px",
    border: "1px solid #ddd",
    borderRadius: 7,
    background: "white",
    fontSize: 14
  },
  alert3: {
    boxSizing: "border-box",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 14,
    maxWidth: 1500,
    padding: "12px 16px",
    margin: "0 auto 16px",
    border: "1px solid #ddd",
    borderRadius: 7,
    background: "#fff9ed",
    fontSize: 14,
    borderColor: "#e7d7b5"
  },
  button3: {
    boxSizing: "border-box",
    border: "1px solid #d7d7d7",
    background: "white",
    color: "#171717",
    borderRadius: 7,
    padding: "9px 14px",
    fontSize: 14,
    fontWeight: 550,
    lineHeight: 1.4,
    whiteSpace: "nowrap",
    fontFamily: "inherit",
    cursor: "pointer"
  },

  disabledButton: {
    cursor: "not-allowed",
    opacity: 0.45
  },
  metricButton: {
    boxSizing: "border-box",
    border: "0",
    borderRight: "1px solid #e8e8e8",
    background: "#fff",
    color: "#171717",
    textAlign: "left",
    padding: "18px 24px",
    minWidth: 0,
    fontFamily: "inherit",
    cursor: "pointer",
    transition: "background .15s ease, box-shadow .15s ease"
  },
  metricButtonActive: {
    background: "#f4f4f4",
    boxShadow: "inset 0 -2px 0 #171717"
  },
  aiSummary: {
    boxSizing: "border-box",
    maxWidth: 1500,
    margin: "0 auto 24px",
    padding: "16px 18px",
    border: "1px solid #e4e4e4",
    borderRadius: 9,
    background: "#fff",
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    gap: 12
  },
  aiSummaryMobile: {
    alignItems: "flex-start",
    flexDirection: "column",
    gap: 10
  },

  aiSummaryLead: {
    boxSizing: "border-box",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 14,
    width: "100%"
  },
  auditButton: {
    boxSizing: "border-box",
    border: "1px solid #d7d7d7",
    background: "#171717",
    color: "#fff",
    borderRadius: 7,
    padding: "8px 12px",
    fontSize: 12,
    fontWeight: 600,
    fontFamily: "inherit",
    cursor: "pointer",
    whiteSpace: "nowrap"
  },
  aiSummaryTitle: {
    display: "block",
    fontSize: 14,
    fontWeight: 650
  },
  aiSummaryText: {
    display: "block",
    color: "#666",
    fontSize: 12,
    marginTop: 2
  },
  aiSummaryStats: {
    boxSizing: "border-box",
    width: "100%",
    display: "grid",
    gridTemplateColumns: "repeat(5,minmax(0,1fr))",
    gap: 8,
    fontSize: 12,
    color: "#555"
  },
  aiSummaryStatsMobile: {
    gridTemplateColumns: "repeat(2,minmax(0,1fr))"
  },
  aiStat: {
    whiteSpace: "nowrap"
  },
  aiStatButton: {
    boxSizing: "border-box",
    border: "1px solid #e6e6e6",
    background: "#fafafa",
    color: "#333",
    borderRadius: 7,
    padding: "9px 10px",
    fontFamily: "inherit",
    fontSize: 12,
    textAlign: "left",
    cursor: "pointer",
    minWidth: 0
  },
  aiStatLabel: {
    display: "block",
    marginTop: 2,
    color: "#777",
    overflowWrap: "anywhere"
  },
  outcomeTrack: {
    boxSizing: "border-box",
    width: "100%",
    height: 6,
    display: "flex",
    overflow: "hidden",
    borderRadius: 999,
    background: "#ededed"
  },
  outcomeApproved: {
    height: "100%",
    background: "#202020"
  },
  outcomeHuman: {
    height: "100%",
    background: "#aaa"
  },
  outcomeRejected: {
    height: "100%",
    background: "#dedede"
  },
  aiPipeline: {
    boxSizing: "border-box",
    width: "100%",
    display: "flex",
    gap: 16,
    flexWrap: "wrap"
  },

  metrics: {
    boxSizing: "border-box",
    display: "grid",
    gridTemplateColumns: "repeat(5,minmax(0,1fr))",
    gap: "0",
    maxWidth: 1500,
    margin: "0 auto 24px",
    background: "#fff",
    border: "1px solid #e4e4e4",
    borderRadius: 10
  },
  metricsMobile: {
    gridTemplateColumns: "repeat(2,minmax(0,1fr))"
  },
  metric: {
    boxSizing: "border-box",
    padding: "18px 24px",
    minWidth: "0"
  },
  metricMobile: {
    padding: "14px 16px"
  },
  span: {
    boxSizing: "border-box",
    display: "block",
    color: "#666",
    fontSize: 14,
    overflowWrap: "anywhere"
  },
  strong: {
    boxSizing: "border-box",
    display: "block",
    fontSize: 30,
    fontWeight: 600,
    letterSpacing: -1,
    marginTop: 4,
    fontVariantNumeric: "tabular-nums"
  },
  strongMobile: {
    fontSize: 26
  },
  layout: {
    boxSizing: "border-box",
    display: "grid",
    gridTemplateColumns: "280px minmax(0,1fr)",
    gap: 24,
    maxWidth: 1500,
    margin: "0 auto",
    alignItems: "start"
  },
  layoutNarrow: {
    gridTemplateColumns: "250px minmax(0,1fr)",
    gap: 16
  },
  layoutMobile: {
    gridTemplateColumns: "1fr"
  },
  panel: {
    boxSizing: "border-box",
    border: "1px solid #e4e4e4",
    borderRadius: 9,
    background: "#fff",
    padding: 20,
    marginBottom: 16
  },
  panelMobile: {
    padding: 18,
    marginBottom: 10
  },
  h2: {
    boxSizing: "border-box",
    margin: "0",
    fontSize: 18,
    fontWeight: 650,
    letterSpacing: "-.3px",
    marginBottom: 15
  },
  label: {
    boxSizing: "border-box",
    display: "block",
    fontSize: 14,
    fontWeight: 550,
    marginBottom: 7
  },
  select: {
    boxSizing: "border-box",
    fontFamily: "inherit",
    width: "100%",
    minWidth: "0",
    padding: "9px 11px",
    border: "1px solid #d8d8d8",
    borderRadius: 6,
    background: "white",
    color: "#171717",
    fontSize: 14,
    lineHeight: 1.5
  },
  hint: {
    boxSizing: "border-box",
    margin: "7px 0 14px",
    fontSize: 13,
    color: "#666",
    lineHeight: 1.5
  },
  summary: {
    boxSizing: "border-box",
    fontSize: 14,
    cursor: "pointer",
    fontWeight: 550
  },
  inlineForm: {
    boxSizing: "border-box",
    display: "flex",
    gap: 6,
    marginTop: 10
  },
  input: {
    boxSizing: "border-box",
    fontFamily: "inherit",
    width: "100%",
    minWidth: "0",
    padding: "9px 11px",
    border: "1px solid #d8d8d8",
    borderRadius: 6,
    background: "white",
    color: "#171717",
    fontSize: 14,
    lineHeight: 1.5,
    flex: 1
  },
  divider: {
    boxSizing: "border-box",
    borderTop: "1px solid #e4e4e4",
    margin: "20px 0"
  },
  textarea: {
    boxSizing: "border-box",
    fontFamily: "inherit",
    width: "100%",
    minWidth: "0",
    padding: "9px 11px",
    border: "1px solid #d8d8d8",
    borderRadius: 6,
    background: "white",
    color: "#171717",
    fontSize: 14,
    lineHeight: 1.5,
    resize: "vertical"
  },
  textareaMobile: {
    minHeight: 100
  },
  panelMobile2: {
    padding: 18
  },
  span2: {
    boxSizing: "border-box",
    float: "right",
    color: "#666",
    fontVariantNumeric: "tabular-nums"
  },
  source: {
    boxSizing: "border-box",
    display: "block",
    width: "100%",
    textAlign: "left",
    border: "0",
    borderBottom: "1px solid #eee",
    borderRadius: 4,
    background: "transparent",
    padding: "12px 5px",
    overflowWrap: "anywhere",
    fontFamily: "inherit",
    cursor: "pointer",
    marginTop: 10
  },
  strong2: {
    boxSizing: "border-box",
    display: "block",
    fontSize: 14,
    fontWeight: 550
  },
  span3: {
    boxSizing: "border-box",
    display: "block",
    fontSize: 12,
    color: "#666",
    marginTop: 3
  },
  aiMethodBar: {
    boxSizing: "border-box",
    display: "flex",
    alignItems: "center",
    gap: 7,
    flexWrap: "wrap",
    padding: "0 22px 16px"
  },
  aiMethodLabel: {
    boxSizing: "border-box",
    fontSize: 12,
    fontWeight: 650,
    color: "#555",
    marginRight: 3
  },
  methodButton: {
    boxSizing: "border-box",
    border: "1px solid #dedede",
    background: "#fff",
    color: "#555",
    borderRadius: 999,
    padding: "5px 10px",
    fontFamily: "inherit",
    fontSize: 12,
    cursor: "pointer"
  },
  methodButtonActive: {
    background: "#171717",
    borderColor: "#171717",
    color: "#fff"
  },
  aiMethodHint: {
    boxSizing: "border-box",
    marginLeft: 4,
    color: "#888",
    fontSize: 11
  },

  workspace: {
    boxSizing: "border-box",
    minWidth: "0",
    background: "#fff",
    border: "1px solid #e4e4e4",
    borderRadius: 9,
    overflow: "hidden"
  },
  toolbar: {
    boxSizing: "border-box",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
    padding: "20px 22px 16px"
  },
  toolbarMobile: {
    padding: "18px 16px 14px"
  },
  h22: {
    boxSizing: "border-box",
    margin: "0",
    fontSize: 18,
    fontWeight: 650,
    letterSpacing: "-.3px"
  },
  filters: {
    boxSizing: "border-box",
    display: "grid",
    gridTemplateColumns: "minmax(150px,1fr) 140px 185px",
    gap: 10,
    padding: "0 22px 18px"
  },
  filtersNarrow: {
    gridTemplateColumns: "1fr 1fr"
  },
  filtersMobile: {
    padding: "0 16px 16px"
  },
  inputNarrow: {
    gridColumn: "1/-1"
  },
  lastSend: {
    boxSizing: "border-box",
    display: "flex",
    justifyContent: "space-between",
    gap: 14,
    padding: "12px 22px",
    background: "#f4f4f4",
    borderTop: "1px solid #e4e4e4",
    fontSize: 13
  },
  lastSendNarrow: {
    flexDirection: "column",
    gap: 6
  },
  lastSendMobile: {
    padding: "12px 16px"
  },
  strong3: {
    boxSizing: "border-box",
    display: "block",
    fontWeight: 550,
    overflowWrap: "anywhere"
  },
  span4: {
    boxSizing: "border-box",
    display: "block",
    color: "#666",
    fontSize: 12,
    marginTop: 2
  },
  actions: {
    boxSizing: "border-box",
    display: "flex",
    gap: 12,
    alignItems: "center",
    flexWrap: "wrap"
  },
  a: {
    boxSizing: "border-box",
    color: "inherit",
    textUnderlineOffset: 3,
    fontSize: 13
  },
  button4: {
    boxSizing: "border-box",
    fontFamily: "inherit",
    cursor: "pointer",
    border: "0",
    background: "transparent",
    padding: "5px 0",
    fontSize: 13,
    color: "#555",
    whiteSpace: "nowrap"
  },
  tableWrap: {
    boxSizing: "border-box",
    width: "100%",
    overflowX: "auto"
  },
  table: {
    boxSizing: "border-box",
    width: "100%",
    borderCollapse: "collapse",
    textAlign: "left",
    fontSize: 14
  },
  tableMobile: {
    display: "block"
  },
  theadMobile: {
    display: "none"
  },
  trMobile: {
    display: "grid",
    gridTemplateColumns: "minmax(0,1fr) auto",
    padding: "14px 16px",
    gap: "9px 12px",
    borderBottom: "1px solid #e4e4e4"
  },
  th: {
    boxSizing: "border-box",
    background: "#f8f8f8",
    padding: "10px 16px",
    borderTop: "1px solid #e4e4e4",
    borderBottom: "1px solid #e4e4e4",
    color: "#666",
    fontSize: 13,
    fontWeight: 550,
    paddingLeft: 22
  },
  thNarrow: {
    paddingLeft: 10,
    paddingRight: 10
  },
  th2: {
    boxSizing: "border-box",
    background: "#f8f8f8",
    padding: "10px 16px",
    borderTop: "1px solid #e4e4e4",
    borderBottom: "1px solid #e4e4e4",
    color: "#666",
    fontSize: 13,
    fontWeight: 550
  },
  srOnly: {
    boxSizing: "border-box",
    position: "absolute",
    width: 1,
    height: 1,
    padding: "0",
    margin: -1,
    overflow: "hidden",
    clip: "rect(0,0,0,0)",
    whiteSpace: "nowrap",
    border: "0"
  },
  td: {
    boxSizing: "border-box",
    padding: "15px 16px",
    borderBottom: "1px solid #ededed",
    verticalAlign: "middle",
    paddingLeft: 22
  },
  tdMobile: {
    display: "block",
    padding: "0",
    border: "0",
    gridColumn: "1",
    gridRow: "1"
  },
  personCell: {
    boxSizing: "border-box",
    display: "flex",
    alignItems: "center",
    gap: 10,
    minWidth: 0
  },
  reviewTd: {
    paddingTop: 22,
    paddingBottom: 22
  },
  reviewPersonCell: {
    flexDirection: "column",
    alignItems: "flex-start",
    gap: 12
  },
  personText: {
    boxSizing: "border-box",
    minWidth: 0
  },
  avatarLink: {
    boxSizing: "border-box",
    display: "block",
    flex: "0 0 auto",
    color: "inherit",
    textDecoration: "none",
    lineHeight: 0
  },
  avatar: {
    boxSizing: "border-box",
    width: 38,
    height: 38,
    borderRadius: "50%",
    objectFit: "cover",
    background: "#f2f2f2",
    flex: "0 0 auto"
  },
  reviewAvatar: {
    width: 320,
    height: 320,
    borderRadius: 12,
    border: "1px solid #e1e1e1",
    boxShadow: "0 4px 18px rgba(0,0,0,.08)"
  },
  reviewAvatarMobile: {
    width: 220,
    height: 220
  },
  avatarFallback: {
    boxSizing: "border-box",
    width: 38,
    height: 38,
    borderRadius: "50%",
    background: "#efefef",
    color: "#666",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 11,
    fontWeight: 650,
    flex: "0 0 auto"
  },
  reviewAvatarFallback: {
    width: 320,
    height: 320,
    borderRadius: 12,
    fontSize: 36,
    border: "1px solid #e1e1e1"
  },
  person: {
    boxSizing: "border-box",
    fontWeight: 600,
    textDecoration: "none",
    overflowWrap: "anywhere",
    color: "inherit",
    textUnderlineOffset: 3
  },
  secondary: {
    boxSizing: "border-box",
    display: "block",
    color: "#666",
    fontSize: 12,
    marginTop: 3,
    overflowWrap: "anywhere"
  },
  td2: {
    boxSizing: "border-box",
    padding: "15px 16px",
    borderBottom: "1px solid #ededed",
    verticalAlign: "middle"
  },
  tdMobile2: {
    display: "block",
    padding: "0",
    border: "0",
    gridColumn: "1",
    gridRow: "2"
  },
  decisionBadge: {
    boxSizing: "border-box",
    border: "1px solid #dedede",
    borderRadius: 999,
    background: "#fff",
    color: "#444",
    fontSize: 12,
    padding: "4px 9px",
    fontFamily: "inherit",
    fontWeight: 600,
    cursor: "pointer",
    whiteSpace: "nowrap"
  },
  decisionApproved: {
    background: "#ededed",
    borderColor: "#dedede",
    color: "#222"
  },
  decisionRejected: {
    background: "#f5f5f5",
    borderColor: "#e3e3e3",
    color: "#555"
  },
  decisionHuman: {
    background: "#fff9ed",
    borderColor: "#e7d7b5",
    color: "#6d531d"
  },
  decisionError: {
    background: "#fff5f4",
    borderColor: "#eed1cc",
    color: "#8d2c22"
  },
  decisionPending: {
    background: "#fafafa",
    borderColor: "#e6e6e6",
    color: "#777"
  },
  aiReason: {
    boxSizing: "border-box",
    display: "block",
    maxWidth: 330,
    color: "#333",
    fontSize: 13,
    lineHeight: 1.4,
    overflowWrap: "anywhere"
  },
  sourceName: {
    boxSizing: "border-box",
    fontSize: 13,
    overflowWrap: "anywhere"
  },
  tdMobile3: {
    display: "block",
    padding: "0",
    border: "0",
    gridColumn: "1",
    gridRow: "3"
  },
  status: {
    boxSizing: "border-box",
    border: "1px solid #dedede",
    borderRadius: 5,
    background: "#fff",
    fontSize: 12,
    padding: "3px 8px",
    color: "#555",
    whiteSpace: "nowrap",
    fontFamily: "inherit",
    cursor: "pointer"
  },
  td3: {
    boxSizing: "border-box",
    padding: "15px 16px",
    borderBottom: "1px solid #ededed",
    verticalAlign: "middle",
    paddingRight: 22
  },
  tdMobile4: {
    display: "block",
    padding: "0",
    border: "0",
    gridColumn: "1",
    gridRow: "4"
  },
  tdMobile5: {
    display: "block",
    padding: "0",
    border: "0",
    gridColumn: "2",
    gridRow: "1/5",
    alignSelf: "center"
  },
  rowActions: {
    boxSizing: "border-box",
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: 12
  },
  reviewRowActions: {
    flexDirection: "column",
    alignItems: "stretch",
    gap: 10,
    minWidth: 132
  },
  reviewActionButton: {
    minWidth: 132,
    minHeight: 48,
    padding: "12px 18px",
    fontSize: 15
  },
  rowActionsNarrow: {
    flexDirection: "column",
    gap: 2,
    alignItems: "flex-end"
  },
  rowActionsMobile: {
    gap: 7
  },
  textButton: {
    boxSizing: "border-box",
    border: "0",
    background: "transparent",
    padding: "5px 0",
    fontSize: 13,
    color: "#555",
    whiteSpace: "nowrap",
    fontFamily: "inherit",
    cursor: "pointer"
  },
  empty: {
    boxSizing: "border-box",
    textAlign: "center",
    padding: "60px 22px"
  },
  h3: {
    boxSizing: "border-box",
    margin: "0",
    fontSize: 16,
    fontWeight: 600
  },
  p2: {
    boxSizing: "border-box",
    margin: "8px 0 18px",
    color: "#666",
    fontSize: 14
  },
  listFooter: {
    boxSizing: "border-box",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "14px 22px",
    gap: 16,
    fontSize: 13,
    color: "#666"
  },
  footnote: {
    boxSizing: "border-box",
    margin: "0",
    padding: "0 22px 18px",
    color: "#666",
    fontSize: 12
  },
  dialog: {
    boxSizing: "border-box",
    width: "min(600px,calc(100vw - 32px))",
    maxHeight: "calc(100dvh - 48px)",
    padding: 25,
    border: "1px solid #ddd",
    borderRadius: 12,
    background: "white",
    color: "#171717",
    overflowY: "auto",
    boxShadow: "0 16px 80px #0002"
  },
  dialogMobile: {
    padding: 20
  },
  dialogHeader: {
    boxSizing: "border-box",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 15,
    marginBottom: 20
  },
  h23: {
    boxSizing: "border-box",
    margin: "0",
    fontSize: 18,
    fontWeight: 650,
    letterSpacing: "-.3px",
    overflowWrap: "anywhere"
  },
  button5: {
    boxSizing: "border-box",
    fontFamily: "inherit",
    cursor: "pointer",
    border: "0",
    background: "transparent",
    fontSize: 26,
    padding: "0 5px",
    color: "#555"
  },
  segment: {
    boxSizing: "border-box",
    display: "flex",
    padding: 4,
    borderRadius: 7,
    background: "#f2f2f2",
    marginBottom: 14
  },
  button6: {
    boxSizing: "border-box",
    fontFamily: "inherit",
    cursor: "pointer",
    flex: 1,
    border: "0",
    background: "transparent",
    color: "#666",
    padding: 9,
    fontSize: 14,
    borderRadius: 5
  },
  dropzone: {
    boxSizing: "border-box",
    display: "block",
    position: "relative",
    textAlign: "center",
    border: "1px dashed #bbb",
    borderRadius: 8,
    padding: "28px 18px",
    margin: "16px 0 20px",
    background: "#fcfcfc",
    cursor: "pointer",
    overflowWrap: "anywhere"
  },
  input2: {
    boxSizing: "border-box",
    fontFamily: "inherit",
    width: "100%",
    minWidth: "0",
    padding: "9px 11px",
    border: "1px solid #d8d8d8",
    borderRadius: 6,
    background: "white",
    color: "#171717",
    fontSize: 14,
    lineHeight: 1.5,
    position: "absolute",
    inset: "0",
    opacity: 0,
    cursor: "pointer"
  },
  strong4: {
    boxSizing: "border-box",
    display: "block",
    fontSize: 15,
    fontWeight: 550
  },
  span5: {
    boxSizing: "border-box",
    display: "block",
    fontSize: 13,
    color: "#666",
    marginTop: 6
  },
  field: {
    boxSizing: "border-box",
    display: "block",
    margin: "16px 0",
    fontSize: 14,
    fontWeight: 550
  },
  input3: {
    boxSizing: "border-box",
    fontFamily: "inherit",
    width: "100%",
    minWidth: "0",
    padding: "9px 11px",
    border: "1px solid #d8d8d8",
    borderRadius: 6,
    background: "white",
    color: "#171717",
    fontSize: 14,
    lineHeight: 1.5,
    display: "block",
    marginTop: 7,
    fontWeight: 400
  },
  hint2: {
    boxSizing: "border-box",
    fontSize: 13,
    color: "#666",
    lineHeight: 1.5,
    margin: "7px 0 14px",
    display: "block",
    fontWeight: 400
  },
  textarea2: {
    boxSizing: "border-box",
    fontFamily: "inherit",
    width: "100%",
    minWidth: "0",
    padding: "9px 11px",
    border: "1px solid #d8d8d8",
    borderRadius: 6,
    background: "white",
    color: "#171717",
    fontSize: 14,
    lineHeight: 1.5,
    resize: "vertical",
    display: "block",
    marginTop: 7,
    fontWeight: 400
  },
  importError: {
    boxSizing: "border-box",
    margin: "0",
    color: "#a13023",
    fontSize: 14,
    marginTop: 14
  },
  importResult: {
    boxSizing: "border-box",
    background: "#f5f5f5",
    border: "1px solid #e5e5e5",
    borderRadius: 7,
    padding: 16,
    marginTop: 18,
    fontSize: 14
  },
  importCounts: {
    boxSizing: "border-box",
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 10,
    margin: "14px 0"
  },
  b: {
    boxSizing: "border-box",
    fontWeight: 650
  },
  p3: {
    boxSizing: "border-box",
    margin: "0"
  },
  ul: {
    boxSizing: "border-box",
    paddingLeft: 20,
    maxHeight: 180,
    overflow: "auto",
    fontSize: 13
  },
  dialogFooter: {
    boxSizing: "border-box",
    display: "flex",
    justifyContent: "flex-end",
    gap: 9,
    marginTop: 24
  },

  detailDecisionRow: {
    boxSizing: "border-box",
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
    marginBottom: 12
  },
  detailActions: {
    boxSizing: "border-box",
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
    marginTop: 14
  },
  scoreGrid: {
    boxSizing: "border-box",
    display: "grid",
    gridTemplateColumns: "repeat(4,minmax(0,1fr))",
    gap: 7,
    margin: "12px 0"
  },
  scoreCard: {
    boxSizing: "border-box",
    border: "1px solid #e8e8e8",
    borderRadius: 7,
    background: "#fafafa",
    padding: "9px 10px"
  },
  scoreValue: {
    boxSizing: "border-box",
    display: "block",
    marginTop: 2,
    fontSize: 16,
    fontWeight: 650
  },
  reasonBox: {
    boxSizing: "border-box",
    marginTop: 12,
    padding: 12,
    border: "1px solid #e5e5e5",
    borderRadius: 7,
    background: "#fafafa"
  },
  reasonTitle: {
    boxSizing: "border-box",
    display: "block",
    fontSize: 13,
    fontWeight: 650,
    marginBottom: 7
  },
  reasonList: {
    boxSizing: "border-box",
    margin: 0,
    paddingLeft: 18,
    fontSize: 13,
    color: "#555"
  },
  reasonItem: {
    boxSizing: "border-box",
    margin: "4px 0"
  },
  p4: {
    boxSizing: "border-box",
    margin: "0",
    color: "#555",
    fontSize: 14
  },
  h32: {
    boxSizing: "border-box",
    margin: "0",
    fontSize: 16,
    fontWeight: 600,
    marginTop: 20,
    marginBottom: 7
  },
  history: {
    boxSizing: "border-box",
    listStyle: "none",
    padding: "0",
    margin: "0"
  },
  li: {
    boxSizing: "border-box",
    padding: "12px 0",
    borderBottom: "1px solid #eee"
  },
  span6: {
    boxSizing: "border-box",
    display: "block",
    fontSize: 13,
    color: "#666",
    marginTop: 3
  }
};
